"use client";

import { useEffect, useRef } from "react";

import Link from "next/link";
import { usePathname } from "next/navigation";

import cn from "classnames";

import { isNavigationMenuLeafItem } from "@/components/header/navigationQuery.types";
import { ChevronDownIcon } from "@/components/icons/chevronDown.icon";
import { Text } from "@/components/text/text.component";

import { appendQueryString } from "@/utils/url.util";

import type { GetNavigationQueryResult } from "@/sanity.types";

import { isActiveShopByHref } from "./siteHeaderShopByMenu.utils";

const SHOP_BY_LINK_CLASS = "group relative py-4 font-bold tracking-wider";

export const SiteHeaderShopByMenu = ({ menu }: SiteHeaderShopByMenuProps) => {
  const pathname = usePathname();
  const detailsRef = useRef<HTMLDetailsElement>(null);
  const links = (menu?.items ?? []).filter(isNavigationMenuLeafItem);
  const itemUrls = links.map((item) => item.link.url);
  const selectedMenuTitle = links.find((item) =>
    isActiveShopByHref(item.link.url, pathname, itemUrls),
  )?.label;

  useEffect(() => {
    const closeIfOutside = (event: Event) => {
      const details = detailsRef.current;
      if (!details?.open) return;
      const target = event.target as Node | null;
      if (!target || details.contains(target)) return;
      details.removeAttribute("open");
    };

    document.addEventListener("pointerdown", closeIfOutside);
    document.addEventListener("focusin", closeIfOutside);
    return () => {
      document.removeEventListener("pointerdown", closeIfOutside);
      document.removeEventListener("focusin", closeIfOutside);
    };
  }, []);

  const renderLinks = () => {
    if (!links.length) return null;

    return (
      <ul className="px-5 py-6" data-testid="site-header-shop-by-menu">
        {links.map((item) => {
          if (!item.link.url) return null;
          const isActive = isActiveShopByHref(
            item.link.url,
            pathname,
            itemUrls,
          );
          return (
            <li key={item._key} className="py-4">
              <Link
                href={
                  appendQueryString(item.link.url, item.link.queryString) ??
                  item.link.url
                }
                target={item.link.openInNewTab ? "_blank" : undefined}
                rel={item.link.openInNewTab ? "noopener noreferrer" : undefined}
                aria-label={item.label}
                aria-current={isActive ? "page" : undefined}
                data-testid="site-header-shop-by-link"
                className={cn(SHOP_BY_LINK_CLASS, {
                  "text-adt-blue": isActive,
                  "text-promo-black": !isActive,
                })}>
                {item.label}
              </Link>
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <details
      ref={detailsRef}
      className="group"
      data-testid="site-header-shop-by">
      <summary
        className="inline-flex cursor-pointer items-center"
        data-toggle="dropdown"
        data-testid="site-header-shop-by-button">
        <div className="flex w-fit items-center gap-2">
          <div className="whitespace-nowrap">
            <Text
              variant="body3"
              className="group-open:hidden"
              data-testid="site-header-shop-by-title">
              {selectedMenuTitle ?? "ADT.com"}
            </Text>
            <span
              className="hidden group-open:flex"
              data-testid="site-header-shop-by-open-title">
              Shop By
            </span>
          </div>
          <div className="h-5 w-5 transition-all duration-500 group-open:rotate-180">
            <ChevronDownIcon
              className="pointer-events-none"
              width={20}
              height={20}
            />
          </div>
        </div>
      </summary>
      <div
        className="absolute z-60 bg-white shadow-lg"
        aria-labelledby="mainNav">
        {renderLinks()}
      </div>
    </details>
  );
};

export type SiteHeaderShopByMenuProps = {
  menu?: GetNavigationQueryResult;
};
